import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import SectionMessage from '@atlaskit/section-message';

import FormTextField from '../../../components/FormTextField';
import FormError, { FormErrorWrapper } from '../../../components/FormError';
import TallButton from '../../../components/TallButton';
import { SignupFieldError } from '../../../types';
import { SignupFormMessages } from '../messages/SignupForm.messages';
import { captureException } from '../../../utilities/analytics/error-reporting';
import { usePrevious } from '../../../utilities/hooks';
import { SignupRecaptcha } from './SignupRecaptcha';
import { EmailFieldGroup, Form, PrivacyPolicyContainer, SignUpButtonWrapper } from './styled';
import { SignupErrorInfo, SignupFieldErrorsWithMessages, UserJourneyContextError } from '../hooks';
import { checkFeatureGate } from '../../../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../../../providers/FeatureGates';

export interface SignupFormData {
  email: string;
}

export type SignupFormProps = {
  email: string | null;
  error: SignupErrorInfo | null | undefined;
  userJourneyContextError: UserJourneyContextError | undefined;
  fieldErrors: SignupFieldErrorsWithMessages;
  hideEmailInput?: boolean;
  disableEmailInput?: boolean;

  /**
   * Called after all validations have passed and the form is ready to subnit
   */
  onSignup: (payload: SignupFormData) => Promise<void>;

  /**
   * Called when the form is clicked, even if form validations have failed.
   */
  onFormClick?: () => void;

  /**
   * Called when the button is clicked, even if form validations have failed.
   */
  onSubmitClick?: () => void;

  onEmailChange?: (username: string) => void;
  onValidationError: (signupFieldError: SignupFieldError) => void;
  title?: React.ReactNode;
  privacyPolicy: React.ReactNode | undefined;
};

export const SignupForm = ({
  title,
  email: emailProp,
  onEmailChange,
  error,
  userJourneyContextError,
  fieldErrors,
  onSignup,
  onSubmitClick,
  onFormClick,
  hideEmailInput,
  disableEmailInput,
  onValidationError,
  privacyPolicy,
}: SignupFormProps) => {
  const { formatMessage } = useIntl();
  const [email, setEmail] = useState(emailProp || '');
  const [shouldShowSpinner, setShouldShowSpinner] = useState(false);

  const emailInputRef = useRef<HTMLInputElement>(null);

  const currentEmailError = fieldErrors.email?.message;
  const previousEmailError = usePrevious(currentEmailError);

  useEffect(() => {
    const emailError = currentEmailError && currentEmailError !== previousEmailError;

    if (emailError) {
      emailInputRef.current?.focus();
    }
  }, [currentEmailError, previousEmailError]);

  const handleSubmit = useCallback(async () => {
    const validationErrors: SignupFieldError = {};

    if (shouldShowSpinner) {
      return;
    }

    if (!hideEmailInput && email.trim() === '') {
      validationErrors.email = formatMessage(SignupFormMessages.emailEmpty);
    }

    if (Object.keys(validationErrors).length) {
      onValidationError(validationErrors);

      const hasEmailError = validationErrors.email;

      if (hasEmailError) {
        emailInputRef.current?.focus();
      }
      return;
    }

    setShouldShowSpinner(true);
    try {
      await onSignup({ email });
    } catch (e) {
      captureException(e);
    }
    setShouldShowSpinner(false);
  }, [shouldShowSpinner, hideEmailInput, email, formatMessage, onValidationError, onSignup]);

  const handleEmailChange = (e: React.FormEvent<HTMLInputElement>) => {
    const email = e.currentTarget.value;

    setEmail(email);

    if (onEmailChange) {
      onEmailChange(email);
    }
  };

  const renderPrivacyPolicy = () => {
    return (
      <>
        <PrivacyPolicyContainer>{privacyPolicy}</PrivacyPolicyContainer>
      </>
    );
  };

  const renderSubmitButton = () => {
    return (
      <SignUpButtonWrapper>
        <TallButton
          appearance="primary"
          type="submit"
          isFullWidth
          id="signup-submit"
          isLoading={shouldShowSpinner}
          onClick={() => onSubmitClick && onSubmitClick()}>
          <FormattedMessage id="signup.form.submit.verify" defaultMessage="Sign up" />
        </TallButton>
      </SignUpButtonWrapper>
    );
  };

  const removeEmailId = checkFeatureGate(AidFrontendFeatureGate.REPLACE_EMAIL_ID_WITH_TEST_ID);

  return (
    <div>
      {userJourneyContextError && (
        <FormErrorWrapper>
          <SectionMessage testId={'redirect-from-resetPassword-error'} appearance="information">
            {userJourneyContextError.errorMessage}
          </SectionMessage>
        </FormErrorWrapper>
      )}

      {error ? <FormError>{error.errorMessage}</FormError> : null}
      {title && <div>{title}</div>}
      <Form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
        id="form-sign-up"
        onClick={onFormClick}>
        <EmailFieldGroup isHidden={hideEmailInput}>
          <FormTextField
            id={removeEmailId ? undefined : 'email'}
            testId="signup-email-idf-testid"
            name="email"
            autoComplete={hideEmailInput ? 'off' : 'username'}
            type="email"
            placeholder={formatMessage(SignupFormMessages.emailPlaceholder)}
            label={formatMessage(SignupFormMessages.emailLabel)}
            autoFocus={!email}
            isInvalid={!!currentEmailError}
            invalidMessage={currentEmailError}
            onChange={handleEmailChange}
            value={email}
            ref={emailInputRef}
            isDisabled={disableEmailInput}
          />
        </EmailFieldGroup>
        {renderPrivacyPolicy()}
        <SignupRecaptcha />
        {renderSubmitButton()}
      </Form>
    </div>
  );
};

export default SignupForm;
